import React, { useState, useEffect } from 'react';
import { enhancedApi } from '../services/api';
import { 
  Settings, 
  Plus,
  Edit,
  Trash2,
  Save,
  X,
  AlertCircle,
  CheckCircle,
  Tag,
  FileText,
  Shield,
  ToggleLeft,
  ToggleRight
} from 'lucide-react';

interface MorbidityCategory {
  id: number;
  name: string;
  description?: string;
  is_active: boolean;
  created_at: string;
  updated_at: string;
}

interface MorbidityAuditIndication {
  id: number;
  name: string;
  description?: string;
  is_active: boolean;
  created_at: string;
  updated_at: string;
}

interface SystemSetting {
  key: string;
  value: string;
  description: string;
}

export const SettingsManagement: React.FC = () => {
  const [activeSubTab, setActiveSubTab] = useState<'categories' | 'indications' | 'system_settings'>('categories');
  const [categories, setCategories] = useState<MorbidityCategory[]>([]);
  const [indications, setIndications] = useState<MorbidityAuditIndication[]>([]);
  const [systemSettings, setSystemSettings] = useState<Record<string, SystemSetting>>({});
  const [loading, setLoading] = useState(false);
  const [showCategoryModal, setShowCategoryModal] = useState(false);
  const [showIndicationModal, setShowIndicationModal] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [deleteType, setDeleteType] = useState<'category' | 'indication'>('category');
  const [itemToDelete, setItemToDelete] = useState<MorbidityCategory | MorbidityAuditIndication | null>(null);
  const [editingCategory, setEditingCategory] = useState<MorbidityCategory | null>(null);
  const [editingIndication, setEditingIndication] = useState<MorbidityAuditIndication | null>(null);
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    is_active: true
  });

  useEffect(() => {
    if (activeSubTab === 'categories') {
      fetchCategories();
    } else if (activeSubTab === 'indications') {
      fetchIndications();
    } else if (activeSubTab === 'system_settings') {
      fetchSystemSettings();
    }
  }, [activeSubTab]);

  const fetchCategories = async () => {
    try {
      setLoading(true);
      const response = await enhancedApi.morbidityCategories.getCategories();
      if (response.success) {
        setCategories(response.data || []);
      }
    } catch (error) {
      console.error('Failed to fetch categories:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchIndications = async () => {
    try {
      setLoading(true);
      const response = await enhancedApi.morbidityAuditIndications.getIndications();
      if (response.success) {
        setIndications(response.data || []);
      }
    } catch (error) {
      console.error('Failed to fetch indications:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchSystemSettings = async () => {
    try {
      setLoading(true);
      const response = await fetch('/api/admin.php?action=system_settings', {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
        },
      });
      const data = await response.json();
      if (data.success) {
        setSystemSettings(data.settings || {});
      }
    } catch (error) {
      console.error('Failed to fetch system settings:', error);
    } finally {
      setLoading(false);
    }
  };

  const updateSystemSetting = async (key: string, value: string) => {
    try {
      const response = await fetch('/api/admin.php?action=update_system_setting', {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          setting_key: key,
          setting_value: value
        }),
      });
      const data = await response.json();
      if (data.success) {
        setSystemSettings(prev => ({
          ...prev,
          [key]: {
            ...prev[key],
            value: value
          }
        }));
      }
    } catch (error) {
      console.error('Failed to update system setting:', error);
    }
  };

  const handleCreateCategory = () => {
    setEditingCategory(null);
    setFormData({ name: '', description: '', is_active: true });
    setShowCategoryModal(true);
  };

  const handleEditCategory = (category: MorbidityCategory) => {
    setEditingCategory(category);
    setFormData({
      name: category.name,
      description: category.description || '',
      is_active: category.is_active
    });
    setShowCategoryModal(true);
  };

  const handleCreateIndication = () => {
    setEditingIndication(null);
    setFormData({ name: '', description: '', is_active: true });
    setShowIndicationModal(true);
  };

  const handleEditIndication = (indication: MorbidityAuditIndication) => {
    setEditingIndication(indication);
    setFormData({
      name: indication.name,
      description: indication.description || '',
      is_active: indication.is_active
    });
    setShowIndicationModal(true);
  };

  const handleSaveCategory = async () => {
    try {
      if (editingCategory) {
        await enhancedApi.morbidityCategories.updateCategory({
          id: editingCategory.id,
          ...formData
        });
      } else {
        await enhancedApi.morbidityCategories.createCategory(formData);
      }
      setShowCategoryModal(false);
      fetchCategories();
    } catch (error) {
      console.error('Failed to save category:', error);
    }
  };

  const handleSaveIndication = async () => {
    try {
      if (editingIndication) {
        await enhancedApi.morbidityAuditIndications.updateIndication({
          id: editingIndication.id,
          ...formData
        });
      } else {
        await enhancedApi.morbidityAuditIndications.createIndication(formData);
      }
      setShowIndicationModal(false);
      fetchIndications();
    } catch (error) {
      console.error('Failed to save indication:', error);
    }
  };

  const handleDeleteCategory = (category: MorbidityCategory) => {
    setItemToDelete(category);
    setDeleteType('category');
    setShowDeleteModal(true);
  };

  const handleDeleteIndication = (indication: MorbidityAuditIndication) => {
    setItemToDelete(indication);
    setDeleteType('indication');
    setShowDeleteModal(true);
  };

  const confirmDelete = async () => {
    if (!itemToDelete) return;
    
    try {
      if (deleteType === 'category') {
        await enhancedApi.morbidityCategories.deleteCategory({ id: itemToDelete.id });
        fetchCategories();
      } else {
        await enhancedApi.morbidityAuditIndications.deleteIndication({ id: itemToDelete.id });
        fetchIndications();
      }
      setShowDeleteModal(false);
      setItemToDelete(null);
    } catch (error) {
      console.error(`Failed to delete ${deleteType}:`, error);
    }
  };

  const resetForm = () => {
    setFormData({ name: '', description: '', is_active: true });
    setEditingCategory(null);
    setEditingIndication(null);
  };

  return (
    <div className="space-y-6">
      {/* Settings Sub-header */}
      <div className="bg-white rounded-2xl shadow-lg border border-gray-100 overflow-hidden">
        <div className="bg-gradient-to-r from-blue-50 via-indigo-50 to-purple-50 px-8 py-6 border-b border-gray-100">
          <div className="flex items-center space-x-4">
            <div className="w-12 h-12 bg-blue-100 rounded-2xl flex items-center justify-center">
              <Settings className="w-6 h-6 text-blue-600" />
            </div>
            <div>
              <h3 className="text-xl font-semibold text-gray-900">Settings Management</h3>
              <p className="text-gray-600">Manage system dropdown options and configurations</p>
            </div>
          </div>
        </div>
      </div>

      {/* Sub-tabs */}
      <div className="bg-white rounded-2xl shadow-lg border border-gray-100">
        <div className="flex border-b">
          <button
            className={`px-6 py-4 font-medium text-sm ${
              activeSubTab === 'categories'
                ? 'border-b-2 border-blue-500 text-blue-600'
                : 'text-gray-500 hover:text-gray-700'
            }`}
            onClick={() => setActiveSubTab('categories')}
          >
            <Tag className="h-4 w-4 inline mr-2" />
            Morbidity Categories
          </button>
          <button
            className={`px-6 py-4 font-medium text-sm ${
              activeSubTab === 'indications'
                ? 'border-b-2 border-blue-500 text-blue-600'
                : 'text-gray-500 hover:text-gray-700'
            }`}
            onClick={() => setActiveSubTab('indications')}
          >
            <FileText className="h-4 w-4 inline mr-2" />
            Audit Indications
          </button>
          <button
            className={`px-6 py-4 font-medium text-sm ${
              activeSubTab === 'system_settings'
                ? 'border-b-2 border-blue-500 text-blue-600'
                : 'text-gray-500 hover:text-gray-700'
            }`}
            onClick={() => setActiveSubTab('system_settings')}
          >
            <Shield className="h-4 w-4 inline mr-2" />
            System Settings
          </button>
        </div>
      </div>

      {/* Categories Tab */}
      {activeSubTab === 'categories' && (
        <div className="space-y-6">
          <div className="bg-white rounded-2xl shadow-lg border border-gray-100 p-6">
            <div className="flex items-center justify-between mb-6">
              <h4 className="text-lg font-semibold text-gray-900 flex items-center space-x-2">
                <Tag className="h-5 w-5 text-blue-600" />
                <span>Morbidity Categories</span>
              </h4>
              <button 
                className="btn btn-primary"
                onClick={handleCreateCategory}
              >
                <Plus className="h-4 w-4" />
                Add Category
              </button>
            </div>

            {loading ? (
              <div className="flex items-center justify-center py-12">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-500"></div>
                <span className="ml-3 text-gray-600">Loading categories...</span>
              </div>
            ) : (
              <div className="grid gap-4">
                {categories.length > 0 ? categories.map((category) => (
                  <div key={category.id} className="bg-gray-50 rounded-xl p-4 border border-gray-200">
                    <div className="flex items-center justify-between">
                      <div className="flex-1">
                        <div className="flex items-center space-x-3">
                          <h5 className="font-semibold text-gray-900">{category.name}</h5>
                          <span className={`badge ${category.is_active ? 'badge-success' : 'badge-error'}`}>
                            {category.is_active ? 'Active' : 'Inactive'}
                          </span>
                        </div>
                        {category.description && (
                          <p className="text-sm text-gray-600 mt-1">{category.description}</p>
                        )}
                        <p className="text-xs text-gray-500 mt-2">
                          Created: {new Date(category.created_at).toLocaleDateString()}
                        </p>
                      </div>
                      <div className="flex gap-2">
                        <button 
                          className="btn btn-sm btn-primary"
                          onClick={() => handleEditCategory(category)}
                        >
                          <Edit className="h-4 w-4" />
                        </button>
                        <button 
                          className="btn btn-sm btn-error"
                          onClick={() => handleDeleteCategory(category)}
                        >
                          <Trash2 className="h-4 w-4" />
                        </button>
                      </div>
                    </div>
                  </div>
                )) : (
                  <div className="text-center py-8 text-gray-500">
                    No categories found. Create your first category to get started.
                  </div>
                )}
              </div>
            )}
          </div>
        </div>
      )}

      {/* Indications Tab */}
      {activeSubTab === 'indications' && (
        <div className="space-y-6">
          <div className="bg-white rounded-2xl shadow-lg border border-gray-100 p-6">
            <div className="flex items-center justify-between mb-6">
              <h4 className="text-lg font-semibold text-gray-900 flex items-center space-x-2">
                <FileText className="h-5 w-5 text-blue-600" />
                <span>Audit Indications</span>
              </h4>
              <button 
                className="btn btn-primary"
                onClick={handleCreateIndication}
              >
                <Plus className="h-4 w-4" />
                Add Indication
              </button>
            </div>

            {loading ? (
              <div className="flex items-center justify-center py-12">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-500"></div>
                <span className="ml-3 text-gray-600">Loading indications...</span>
              </div>
            ) : (
              <div className="grid gap-4">
                {indications.length > 0 ? indications.map((indication) => (
                  <div key={indication.id} className="bg-gray-50 rounded-xl p-4 border border-gray-200">
                    <div className="flex items-center justify-between">
                      <div className="flex-1">
                        <div className="flex items-center space-x-3">
                          <h5 className="font-semibold text-gray-900">{indication.name}</h5>
                          <span className={`badge ${indication.is_active ? 'badge-success' : 'badge-error'}`}>
                            {indication.is_active ? 'Active' : 'Inactive'}
                          </span>
                        </div>
                        {indication.description && (
                          <p className="text-sm text-gray-600 mt-1">{indication.description}</p>
                        )}
                        <p className="text-xs text-gray-500 mt-2">
                          Created: {new Date(indication.created_at).toLocaleDateString()}
                        </p>
                      </div>
                      <div className="flex gap-2">
                        <button 
                          className="btn btn-sm btn-primary"
                          onClick={() => handleEditIndication(indication)}
                        >
                          <Edit className="h-4 w-4" />
                        </button>
                        <button 
                          className="btn btn-sm btn-error"
                          onClick={() => handleDeleteIndication(indication)}
                        >
                          <Trash2 className="h-4 w-4" />
                        </button>
                      </div>
                    </div>
                  </div>
                )) : (
                  <div className="text-center py-8 text-gray-500">
                    No indications found. Create your first indication to get started.
                  </div>
                )}
              </div>
            )}
          </div>
        </div>
      )}

      {/* System Settings Tab */}
      {activeSubTab === 'system_settings' && (
        <div className="space-y-6">
          <div className="bg-white rounded-2xl shadow-lg border border-gray-100 p-6">
            <div className="flex items-center justify-between mb-6">
              <h4 className="text-lg font-semibold text-gray-900 flex items-center space-x-2">
                <Shield className="h-5 w-5 text-blue-600" />
                <span>System Settings</span>
              </h4>
            </div>

            {loading ? (
              <div className="flex items-center justify-center py-12">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-500"></div>
                <span className="ml-3 text-gray-600">Loading settings...</span>
              </div>
            ) : (
              <div className="space-y-6">
                {/* Allow Edit Approved Audits Setting */}
                <div className="bg-gray-50 rounded-xl p-6 border border-gray-200">
                  <div className="flex items-center justify-between">
                    <div className="flex-1">
                      <div className="flex items-center space-x-3 mb-2">
                        <h5 className="font-semibold text-gray-900">Allow Editing of Approved Audits</h5>
                        <span className="badge badge-info">System Setting</span>
                      </div>
                      <p className="text-sm text-gray-600 mb-4">
                        When enabled, reviewers and superusers can edit audits even after they have been approved. 
                        This setting only affects users with reviewer or superuser roles - auditors cannot edit approved audits.
                      </p>
                      <div className="flex items-center space-x-4">
                        <div className="flex items-center space-x-2">
                          <span className="text-sm font-medium text-gray-700">Current Status:</span>
                          <span className={`badge ${systemSettings.allow_edit_approved_audits?.value === 'true' ? 'badge-success' : 'badge-error'}`}>
                            {systemSettings.allow_edit_approved_audits?.value === 'true' ? 'Enabled' : 'Disabled'}
                          </span>
                        </div>
                      </div>
                    </div>
                    <div className="ml-6">
                      <button
                        onClick={() => {
                          const newValue = systemSettings.allow_edit_approved_audits?.value === 'true' ? 'false' : 'true';
                          updateSystemSetting('allow_edit_approved_audits', newValue);
                        }}
                        className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 ${
                          systemSettings.allow_edit_approved_audits?.value === 'true' ? 'bg-blue-600' : 'bg-gray-200'
                        }`}
                      >
                        <span
                          className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                            systemSettings.allow_edit_approved_audits?.value === 'true' ? 'translate-x-6' : 'translate-x-1'
                          }`}
                        />
                      </button>
                    </div>
                  </div>
                </div>

                {/* Other System Settings */}
                {Object.entries(systemSettings).filter(([key]) => key !== 'allow_edit_approved_audits').map(([key, setting]) => (
                  <div key={key} className="bg-gray-50 rounded-xl p-6 border border-gray-200">
                    <div className="flex items-center justify-between">
                      <div className="flex-1">
                        <div className="flex items-center space-x-3 mb-2">
                          <h5 className="font-semibold text-gray-900 capitalize">
                            {key.replace(/_/g, ' ')}
                          </h5>
                          <span className="badge badge-info">System Setting</span>
                        </div>
                        <p className="text-sm text-gray-600 mb-4">
                          {setting.description}
                        </p>
                        <div className="flex items-center space-x-4">
                          <div className="flex items-center space-x-2">
                            <span className="text-sm font-medium text-gray-700">Current Value:</span>
                            <span className="badge badge-outline">
                              {setting.value}
                            </span>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                ))}

                {Object.keys(systemSettings).length === 0 && (
                  <div className="text-center py-8 text-gray-500">
                    No system settings found.
                  </div>
                )}
              </div>
            )}
          </div>
        </div>
      )}

      {/* Category Modal */}
      {showCategoryModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-2xl shadow-2xl w-full max-w-md mx-4">
            <div className="p-6">
              <div className="flex items-center justify-between mb-6">
                <h3 className="text-lg font-semibold text-gray-900">
                  {editingCategory ? 'Edit Category' : 'Add New Category'}
                </h3>
                <button 
                  className="text-gray-400 hover:text-gray-600"
                  onClick={() => {
                    setShowCategoryModal(false);
                    resetForm();
                  }}
                >
                  <X className="h-5 w-5" />
                </button>
              </div>

              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Category Name *
                  </label>
                  <input
                    type="text"
                    className="input input-bordered w-full"
                    value={formData.name}
                    onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                    placeholder="Enter category name"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Description
                  </label>
                  <textarea
                    className="textarea textarea-bordered w-full"
                    rows={3}
                    value={formData.description}
                    onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                    placeholder="Enter category description (optional)"
                  />
                </div>

                <div className="flex items-center space-x-2">
                  <input
                    type="checkbox"
                    id="is_active"
                    className="checkbox checkbox-primary"
                    checked={formData.is_active}
                    onChange={(e) => setFormData({ ...formData, is_active: e.target.checked })}
                  />
                  <label htmlFor="is_active" className="text-sm font-medium text-gray-700">
                    Active
                  </label>
                </div>
              </div>

              <div className="flex gap-3 mt-6">
                <button 
                  className="btn btn-outline flex-1"
                  onClick={() => {
                    setShowCategoryModal(false);
                    resetForm();
                  }}
                >
                  Cancel
                </button>
                <button 
                  className="btn btn-primary flex-1"
                  onClick={handleSaveCategory}
                  disabled={!formData.name.trim()}
                >
                  <Save className="h-4 w-4" />
                  {editingCategory ? 'Update' : 'Create'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Indication Modal */}
      {showIndicationModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-2xl shadow-2xl w-full max-w-md mx-4">
            <div className="p-6">
              <div className="flex items-center justify-between mb-6">
                <h3 className="text-lg font-semibold text-gray-900">
                  {editingIndication ? 'Edit Indication' : 'Add New Indication'}
                </h3>
                <button 
                  className="text-gray-400 hover:text-gray-600"
                  onClick={() => {
                    setShowIndicationModal(false);
                    resetForm();
                  }}
                >
                  <X className="h-5 w-5" />
                </button>
              </div>

              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Indication Name *
                  </label>
                  <input
                    type="text"
                    className="input input-bordered w-full"
                    value={formData.name}
                    onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                    placeholder="Enter indication name"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Description
                  </label>
                  <textarea
                    className="textarea textarea-bordered w-full"
                    rows={3}
                    value={formData.description}
                    onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                    placeholder="Enter indication description (optional)"
                  />
                </div>

                <div className="flex items-center space-x-2">
                  <input
                    type="checkbox"
                    id="is_active_indication"
                    className="checkbox checkbox-primary"
                    checked={formData.is_active}
                    onChange={(e) => setFormData({ ...formData, is_active: e.target.checked })}
                  />
                  <label htmlFor="is_active_indication" className="text-sm font-medium text-gray-700">
                    Active
                  </label>
                </div>
              </div>

              <div className="flex gap-3 mt-6">
                <button 
                  className="btn btn-outline flex-1"
                  onClick={() => {
                    setShowIndicationModal(false);
                    resetForm();
                  }}
                >
                  Cancel
                </button>
                <button 
                  className="btn btn-primary flex-1"
                  onClick={handleSaveIndication}
                  disabled={!formData.name.trim()}
                >
                  <Save className="h-4 w-4" />
                  {editingIndication ? 'Update' : 'Create'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Delete Modal */}
      {showDeleteModal && itemToDelete && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-2xl shadow-2xl p-6 w-full max-w-md mx-4">
            <div className="flex items-center gap-3 mb-4">
              <div className="w-10 h-10 bg-red-100 rounded-full flex items-center justify-center">
                <Trash2 className="w-5 h-5 text-red-600" />
              </div>
              <div>
                <h3 className="text-lg font-semibold text-gray-900">
                  Delete {deleteType === 'category' ? 'Category' : 'Indication'}
                </h3>
                <p className="text-sm text-gray-500">This action cannot be undone</p>
              </div>
            </div>
            
            <div className="mb-6">
              <p className="text-gray-700 mb-4">
                Are you sure you want to permanently delete this {deleteType}?
              </p>
              <div className="bg-gray-50 p-4 rounded-lg">
                <div className="text-sm">
                  <div className="mb-2">
                    <span className="font-medium text-gray-700">Name:</span>
                    <p className="text-gray-900">{itemToDelete.name}</p>
                  </div>
                  {itemToDelete.description && (
                    <div className="mb-2">
                      <span className="font-medium text-gray-700">Description:</span>
                      <p className="text-gray-900">{itemToDelete.description}</p>
                    </div>
                  )}
                  <div>
                    <span className="font-medium text-gray-700">Status:</span>
                    <p className="text-gray-900">{itemToDelete.is_active ? 'Active' : 'Inactive'}</p>
                  </div>
                </div>
              </div>
              <p className="text-sm text-red-600 mt-3">
                ⚠️ This will permanently remove the {deleteType} and all associated data.
              </p>
            </div>
            
            <div className="flex justify-end gap-3">
              <button 
                onClick={() => {
                  setShowDeleteModal(false);
                  setItemToDelete(null);
                }}
                className="px-4 py-2 text-gray-700 bg-gray-100 hover:bg-gray-200 rounded-lg transition-colors"
              >
                Cancel
              </button>
              <button 
                onClick={confirmDelete}
                className="px-4 py-2 bg-red-600 hover:bg-red-700 text-white rounded-lg transition-colors"
              >
                <Trash2 className="h-4 w-4 mr-2 inline" />
                Delete {deleteType === 'category' ? 'Category' : 'Indication'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};
